import { Download, Eye, History } from "lucide-react";

import { cn } from "@/lib/utils";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { Button } from "@/shared/components/form/Button";
import { InputSelect } from "@/shared/components/form/InputSelect";
import type { AuditTranslations } from "@/shared/lang/interfaces/audit";

import type { AuditoriaRow, AuditoriaAccion } from "../types/auditoria.types";
import { formatNumericDateTime } from "../utils/formatNumericDateTime";

type SelectOption = { label: string; value: string };

interface PaymentAuditTableProps {
  rows: AuditoriaRow[];
  totalItems: number;
  itemsPerPage: number;
  paginationOptions: SelectOption[];
  onItemsPerPageChange: (value: string) => void;
  onViewArchivos: (row: AuditoriaRow) => void;
  onViewHistory: (entityId: string, entityType: string) => void;
  canExport: boolean;
  isExporting: boolean;
  onDownloadExcel: () => void;
  t: (k: keyof AuditTranslations, opts?: { count?: number; total?: number }) => string;
}

function getClientDisplay(row: AuditoriaRow): string {
  return row.tableOrganizationName ?? row.tableClientName;
}

function getSubclientDisplay(row: AuditoriaRow): string {
  return row.tableSubclientName || "—";
}

function getEstadoBadgeClass(estado: string): string {
  const key = (estado || "").toUpperCase();
  switch (key) {
    case "PENDING":
      return "bg-amber-50 dark:bg-amber-500/20 text-amber-800 dark:text-amber-200";
    case "IN_PROGRESS":
      return "bg-blue-50 dark:bg-blue-500/20 text-blue-800 dark:text-blue-200";
    case "PAID":
      return "bg-emerald-50 dark:bg-emerald-500/20 text-emerald-800 dark:text-emerald-200";
    case "CONFIRMED":
      return "bg-teal-50 dark:bg-teal-500/20 text-teal-800 dark:text-teal-200";
    case "CANCELLED":
      return "bg-red-50 dark:bg-red-500/20 text-red-800 dark:text-red-200";
    case "ARCHIVED":
      return "bg-slate-100 dark:bg-slate-600/50 text-slate-700 dark:text-slate-200";
    case "FILED":
      return "bg-indigo-50 dark:bg-indigo-500/20 text-indigo-800 dark:text-indigo-200";
    default:
      return "bg-slate-100 dark:bg-slate-600/50 text-slate-700 dark:text-slate-200";
  }
}

function getEstadoLabel(estado: string, t: (k: keyof AuditTranslations) => string): string {
  const key = (estado || "").toUpperCase();
  if (key === "FILED") return t("table_estado_filed");
  return estado;
}

function isArchivosButtonEnabled(row: AuditoriaRow): boolean {
  if (row.archivosUrls.length > 0) return true;
  const accion: AuditoriaAccion = row.accion;
  return (
    accion === "creacion" ||
    accion === "cambio_estado" ||
    accion === "actualizacion_datos" ||
    accion === "eliminacion"
  );
}

export const PaymentAuditTable = ({
  rows,
  totalItems,
  itemsPerPage,
  paginationOptions,
  onItemsPerPageChange,
  onViewArchivos,
  onViewHistory,
  canExport,
  isExporting,
  onDownloadExcel,
  t,
}: PaymentAuditTableProps) => {
  const showingText =
    rows.length === 1
      ? t("payments_table_showing_one", { total: totalItems })
      : t("payments_table_showing_many", { count: rows.length, total: totalItems });

  return (
    <>
      {/* The row wraps only between the count and the controls. The controls themselves must not
          wrap internally: "Mostrar:" and its select are one reading, and splitting them leaves a
          stray label above a stray dropdown. */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-zinc-800 flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
        <span className="text-sm text-gray-700 dark:text-gray-300">{showingText}</span>

        <div className="flex flex-wrap items-center justify-end gap-x-5 gap-y-3">
          <ColocaButton
            type="button"
            variant="default"
            icon={Download}
            loading={isExporting}
            disabled={!canExport}
            onClick={onDownloadExcel}
            // The hint explains an EMPTY table only: `canExport` is also false while a page loads or the
            // report downloads, and "apply filters with results" is false said then.
            title={rows.length === 0 ? t("payments_export_disabled_hint") : undefined}
          >
            {t("payments_export_button")}
          </ColocaButton>

          <div className="flex shrink-0 items-center gap-2">
            <span className="whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
              {t("table_show_label")}
            </span>
            {/* `InputSelect`'s root is `w-full`, so in a wrapping flex row it demands a line of its
                own and drops below the label. Bounding it here gives it an intrinsic width instead;
                `max-w-full` keeps the component's own cap out of the way.
                96px is measured, not guessed: react-select spends ~36px on the chevron and ~16px on
                padding, so a narrower box truncates the widest option ("100"). */}
            <div className="w-24">
              <InputSelect<SelectOption>
                options={paginationOptions}
                value={paginationOptions.find((opt) => opt.value === String(itemsPerPage)) ?? null}
                onChange={(selected) => {
                  const value = Array.isArray(selected) ? selected[0]?.value : selected?.value;
                  onItemsPerPageChange(value ?? "25");
                }}
                maxWidth="max-w-full"
                small
              />
            </div>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px]">
          <thead className="bg-gray-50 dark:bg-zinc-800">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t("table_th_date")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t("payments_table_th_client")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t("payments_table_th_subclient")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t("table_th_currency")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t("table_th_amount")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t("table_th_estado")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t("table_th_archivos")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t("table_th_actions")}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-zinc-900 divide-y divide-gray-200 dark:divide-zinc-800">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
                  {t("payments_table_empty")}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-zinc-800/50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {formatNumericDateTime(row.fecha)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100 max-w-[220px]">
                    <span className="block truncate" title={getClientDisplay(row)}>
                      {getClientDisplay(row)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100 max-w-[200px]">
                    <span className="block truncate" title={getSubclientDisplay(row)}>
                      {getSubclientDisplay(row)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                    {row.tableCurrency}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {row.tableAmount}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={cn(
                        "inline-flex px-2.5 py-1 text-xs font-semibold rounded-md",
                        getEstadoBadgeClass(row.estado),
                      )}
                    >
                      {getEstadoLabel(row.estado, t)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex items-center justify-center">
                      <div className="relative group">
                        <button
                          type="button"
                          className="inline-flex items-center justify-center rounded-full p-2 border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-500/40 dark:bg-emerald-500/15 dark:text-emerald-100 dark:hover:bg-emerald-500/25 transition-colors shadow-[0_1px_2px_rgba(0,0,0,0.04)] disabled:opacity-50 disabled:pointer-events-none"
                          aria-label={t("table_tooltip_view_archivos")}
                          onClick={() => onViewArchivos(row)}
                          disabled={!isArchivosButtonEnabled(row)}
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 text-xs font-medium text-white bg-black dark:bg-zinc-900 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                          {t("table_tooltip_view_archivos")}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <Button
                      variant="outline"
                      onClick={() => onViewHistory(row.entityId, row.entityType)}
                      type="button"
                      className="flex items-center gap-2"
                    >
                      <History className="h-4 w-4" />
                      {t("table_button_view_history")}
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};
